import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, Clock, CheckCircle, Send } from 'lucide-react';

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
  </svg>
);

const services = [
  'Hair Braiding & Styling',
  'Natural Hair Treatment',
  'Hair Coloring & Highlights',
  'Relaxer & Keratin Treatments',
  'Facials & Skincare',
  'Manicure & Pedicure',
  'Full Body Massage & Spa',
  'Makeup & Bridal Packages',
];

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

function InputField({ label, type = 'text', name, value, onChange, required, placeholder }) {
  return (
    <div className="group">
      <label className="block font-inter text-xs font-semibold text-charcoal/60 tracking-wider uppercase mb-2">
        {label} {required && <span className="text-roseGold">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-3.5 rounded-xl border border-champagne bg-white/60 font-inter text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-roseGold focus:ring-2 focus:ring-roseGold/15 transition-all duration-300 hover:border-roseGold/40"
      />
    </div>
  );
}

export default function Booking() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const [form, setForm] = useState({
    name: '',
    phone: '',
    service: '',
    date: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate submission — in production, connect to backend or email service
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  const contactItems = [
    {
      icon: <MapPin className="w-5 h-5 text-roseGold" />,
      label: 'Address',
      value: 'CVS Plaza, Adetokunbo Ademola Crescent, Wuse 2, Abuja, Nigeria',
    },
    {
      icon: <Phone className="w-5 h-5 text-roseGold" />,
      label: 'Phone',
      value: '+234 901 828 7564',
      href: 'tel:+2349018287564',
    },
    {
      icon: <Clock className="w-5 h-5 text-roseGold" />,
      label: 'Hours',
      value: 'Mon – Sat: 8:00 AM – 8:00 PM\nSun: 10:00 AM – 6:00 PM',
    },
  ];

  return (
    <section
      id="booking"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #FDF8F5 0%, #F5E6D3 100%)' }}
    >
      {/* BG decoration */}
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C9956C, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-60 h-60 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6B2737, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-inter text-roseGold text-xs font-semibold tracking-[0.3em] uppercase mb-3"
          >
            Get In Touch
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-5"
          >
            Book Your Appointment
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-20 h-0.5 bg-gradient-to-r from-roseGold to-burgundy mx-auto mb-5"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-inter text-charcoal/70 text-base max-w-xl mx-auto"
          >
            Ready to experience Abuja's finest salon? Fill in the form below or reach us directly via WhatsApp.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* LEFT — Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Contact cards */}
            <div className="space-y-5 mb-10">
              {contactItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-4 bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-champagne shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-champagne flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <p className="font-inter text-xs font-semibold text-charcoal/50 tracking-wider uppercase mb-1">
                      {item.label}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="font-inter text-sm text-charcoal font-medium hover:text-roseGold transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="font-inter text-sm text-charcoal font-medium whitespace-pre-line leading-relaxed">
                        {item.value}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <motion.a
              href="https://wa.me/2349018287564"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex items-center gap-3 w-full bg-[#25D366] hover:bg-[#20ba59] text-white font-inter font-semibold text-sm py-4 px-6 rounded-2xl shadow-lg shadow-green-500/20 hover:shadow-green-500/35 hover:scale-[1.02] transition-all duration-300 mb-8"
            >
              <WhatsAppIcon className="w-6 h-6" />
              Book Instantly via WhatsApp
              <span className="ml-auto text-xs font-normal opacity-80">+234 901 828 7564</span>
            </motion.a>

            {/* Social icons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <p className="font-inter text-xs font-semibold text-charcoal/50 tracking-wider uppercase mb-4">
                Follow Us
              </p>
              <div className="flex gap-3">
                {[
                  {
                    label: 'Instagram',
                    icon: <InstagramIcon className="w-4 h-4" />,
                    color: 'from-purple-500 to-pink-500',
                    href: '#',
                  },
                  {
                    label: 'WhatsApp',
                    icon: <WhatsAppIcon className="w-4 h-4" />,
                    color: 'from-green-500 to-green-400',
                    href: 'https://wa.me/2349018287564',
                  },
                  {
                    label: 'Facebook',
                    icon: <FacebookIcon className="w-4 h-4" />,
                    color: 'from-blue-600 to-blue-500',
                    href: '#',
                  },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${social.color} flex items-center justify-center text-white shadow-md hover:scale-110 hover:shadow-lg transition-all duration-300`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT — Booking form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-champagne shadow-xl shadow-roseGold/10"
          >
            {submitted ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex flex-col items-center justify-center text-center py-10 gap-5"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-roseGold to-burgundy flex items-center justify-center shadow-lg shadow-roseGold/30">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="font-playfair text-2xl font-bold text-dark mb-2">
                    Request Received!
                  </h3>
                  <p className="font-inter text-charcoal/70 text-sm leading-relaxed max-w-xs">
                    Thank you! We'll confirm your appointment via WhatsApp or phone within 24 hours.
                  </p>
                </div>
                <a
                  href="https://wa.me/2349018287564"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-inter font-semibold text-sm hover:bg-[#20ba59] transition-colors shadow-md"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  Chat on WhatsApp
                </a>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h3 className="font-playfair text-xl font-bold text-dark mb-1">
                    Make a Booking
                  </h3>
                  <p className="font-inter text-charcoal/60 text-xs">
                    Fill in your details and we'll be in touch shortly.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <InputField
                    label="Full Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Adaeze Okonkwo"
                  />
                  <InputField
                    label="Phone Number"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    placeholder="+234 800 000 0000"
                  />
                </div>

                {/* Service dropdown */}
                <div className="group">
                  <label className="block font-inter text-xs font-semibold text-charcoal/60 tracking-wider uppercase mb-2">
                    Service <span className="text-roseGold">*</span>
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3.5 rounded-xl border border-champagne bg-white/60 font-inter text-sm text-charcoal focus:outline-none focus:border-roseGold focus:ring-2 focus:ring-roseGold/15 transition-all duration-300 hover:border-roseGold/40 appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23C9956C' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Preferred date */}
                <InputField
                  label="Preferred Date"
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />

                {/* Message */}
                <div>
                  <label className="block font-inter text-xs font-semibold text-charcoal/60 tracking-wider uppercase mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Any special requests or details about your hair/skin…"
                    className="w-full px-4 py-3.5 rounded-xl border border-champagne bg-white/60 font-inter text-sm text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-roseGold focus:ring-2 focus:ring-roseGold/15 transition-all duration-300 hover:border-roseGold/40 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-roseGold to-roseGoldDark text-white font-inter font-semibold text-sm tracking-wide shadow-lg shadow-roseGold/30 hover:shadow-roseGold/50 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Booking Request
                    </>
                  )}
                </button>

                <p className="font-inter text-charcoal/40 text-xs text-center">
                  Or reach us instantly on{' '}
                  <a
                    href="https://wa.me/2349018287564"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-roseGold hover:underline"
                  >
                    WhatsApp
                  </a>
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
